import type { Modal } from '@ps/web-core-store';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import BodyClassName from 'react-body-classname';
import Loadable from 'react-loadable';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import type { RouteComponentProps } from 'react-router';

import StarsAccount, { StarsQuiz } from '@swf/account';
import { PasswordRecoveryWizard, StarsAuth } from '@swf/auth';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import MitIdRedirect from '@swf/components/MitIdRedirect/MitIdRedirect';
import BetDelay from '@swf/components/Modals/BetDelay/BetDelay';
import LoggedOut from '@swf/components/Modals/LoggedOut/LoggedOut';
import RedirectNotification from '@swf/components/RedirectNotification/RedirectNotification';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import SecurityInvestigation from '@swf/components/SecurityInvestigation/SecurityInvestigation';
import StarsCrm from '@swf/components/StarsCrm/StarsCrm';
import StarsMPNotification from '@swf/components/StarsMPNotification/StarsMPNotification';
import NotFound from '@swf/components/StarsModal/NotFound';
import useLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { StaticContext } from '@swf/core/types/StaticContext';
import { encodeQuery } from '@swf/core/utils';
import { PromotionsAndRewardsHub } from '@swf/martech';
import { StarsNotification } from '@swf/notification';

import type { ModalContextValue } from '../StarsModal/Context';
import { ModalContext } from '../StarsModal/Context';

import styles from './StarsModalRoutes.scss';

type Props = ModalContextValue;

const LoadableHelpModalRoutes = Loadable({
  loader: () => import('@swf/help'),
  loading: () => null,
  render: ({ HelpModalRoutes }) => <HelpModalRoutes />
});

const LoadableBespokeLobbyModalRoutes = Loadable({
  loader: () => import('../../../widgets/poker-bespoke-lobby-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadableSingleTournamentModalRoutes = Loadable({
  loader: () =>
    import('../../../widgets/poker-single-tournament-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadableHomeGamesModalRoutes = Loadable({
  loader: () => import('../../../widgets/poker-home-games-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadablePokerCommonModalRoutes = Loadable({
  loader: () => import('@swf/poker'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadablePokerLobbyRoutes = Loadable({
  loader: () => import('../../../widgets/poker-lobby-template-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadablePowerPathModalRoutes = Loadable({
  loader: () =>
    import('../../../widgets/poker-power-path-widget/components/ModalRoutes/PowerPathModalRoutes'),
  loading: () => null,
  render: ({ PowerPathModalRoutes }) => <PowerPathModalRoutes />
});

const LoadableSpinAndGoModalRoutes = Loadable({
  loader: () => import('../../../widgets/poker-spin-and-go-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ ModalRoutes }) => <ModalRoutes />
});

const LoadableMultiTableTournamentsModalRoutes = Loadable({
  loader: () =>
    import('../../../widgets/poker-multi-table-tournaments-widget/components/Modal/ModalRoutes'),
  loading: () => null,
  render: ({ MultiTableTournamentsModalRoutes }) => <MultiTableTournamentsModalRoutes />
});

const LoadableCasinoModalRoutes = Loadable({
  loader: () => import('@swf/casino'),
  loading: () => null,
  render: ({ CasinoModal }, props: RouteComponentProps) => <CasinoModal {...props} />
});

const emptyModal: Partial<Modal> = {
  /* path: '', locationState: undefined  */
};

const AuthRoute: React.FC<RouteComponentProps<object, StaticContext>> = ({
  location,
  match,
  staticContext
}) => {
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isEmbeddedAccount = useSelector(select.location.isEmbeddedAccount);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const locale = useSelector(select.locale.get);

  switch (true) {
    case isEmbeddedAccount: {
      /*
        temporary solution for redirecting from embedded/account to embedded/modal/account
        currently there are a lot of places where embedded/account is used also in native clients
      **/
      if (staticContext) {
        staticContext.statusCode = 301;
        staticContext.url = `/${locale}/embedded/modal${
          location.pathname === '/' ? '' : location.pathname
        }`;
      }
      return null;
    }
    case hasClientSession && !isAuthenticated:
      return <Redirect to={{ pathname: '/auth/login', state: { path: location.pathname } }} />;
    case !isAuthenticated:
      return null;
    default:
      return match.path === '/account' ? <StarsAccount /> : <StarsQuiz />;
  }
};

const StarsModalRoutes: React.FC<Props> = ({ setStyle }) => {
  const history = useHistory();
  const location = useLocation<Modal>();
  const dispatch = useDispatch<Dispatch>();
  const modal = useSelector(select.modal.getCurrent) || emptyModal;
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const prevPath = useRef<string>(undefined);
  const prevModal = useRef<Partial<Modal>>(emptyModal);
  const query = useSelector(select.location.getQuery);
  const search = !isEmpty(modal?.locationState)
    ? encodeQuery(modal.locationState as object)
    : location.search;
  const {
    modal: { unqueuePSNPartnerNotification }
  } = useDispatch<Dispatch>();

  useEffect(() => {
    const psnPartnerNotificationChannel = new BroadcastChannel('psn-partner-notification-modal');
    psnPartnerNotificationChannel.onmessage = ({ data }) => {
      unqueuePSNPartnerNotification({ path: '/notifications/psn-partner', notification: data });
    };
    return () => psnPartnerNotificationChannel.close();
  }, [unqueuePSNPartnerNotification]);

  useLayoutEffect(() => {
    if (!modal.path && !location.pathname) return;

    if (prevModal.current.path !== modal.path && modal.path !== location.pathname) {
      history.replace({
        pathname: modal.path || location?.state?.path || '/',
        state: modal.locationState || location?.state?.locationState,
        search
      });
    } else if (location.pathname !== prevPath.current && modal.path !== location.pathname) {
      dispatch.modal.replace([
        { path: location.pathname, locationState: { ...location.state, ...query } }
      ]);
    } else if (
      location.pathname !== '/auth/login' &&
      prevModal.current.locationState &&
      modal.locationState &&
      prevModal.current.locationState !== modal.locationState
    ) {
      history.replace({
        pathname: modal.path || location?.state?.path || '/',
        state: modal.locationState,
        search
      });
    }

    prevPath.current = location.pathname;
    prevModal.current = modal;
  }, [dispatch.modal, history, location.pathname, location.state, search, modal, query]);

  return (
    <ModalContext.Provider value={{ setStyle }}>
      <CacheControl>public, max-age=86400</CacheControl>
      <BodyClassName className={styles.body} />
      <ErrorBoundary fallback={<Route component={NotFound} />}>
        <Switch location={location}>
          {!isAuthenticated && (
            <Route path="/auth/password-recovery" component={PasswordRecoveryWizard} />
          )}
          <Route path="/auth" component={StarsAuth} />
          <Route path="/help" component={LoadableHelpModalRoutes} />
          <Route path="/casino" component={LoadableCasinoModalRoutes} />
          <Route path="/notifications/redirected" component={RedirectNotification} />
          <Route path="/notifications" component={StarsNotification} />
          <Route path="/mpnotifications" component={StarsMPNotification} />
          <Route path="/prhub" component={PromotionsAndRewardsHub} />
          <Route path="/security-investigation" component={SecurityInvestigation} />
          <Route path="/stars-crm/:zoneID" component={StarsCrm} />
          <Route path="/homegames" component={LoadableHomeGamesModalRoutes} />
          <Route path="/bespokelobby" component={LoadableBespokeLobbyModalRoutes} />
          <Route path="/single-tourn" component={LoadableSingleTournamentModalRoutes} />
          <Route path="/poker" component={LoadablePokerCommonModalRoutes} />
          <Route path="/spinandgo" component={LoadableSpinAndGoModalRoutes} />
          <Route path="/poker-lobby" component={LoadablePokerLobbyRoutes} />
          <Route path="/powerpath" component={LoadablePowerPathModalRoutes} />
          <Route path="/mtt" component={LoadableMultiTableTournamentsModalRoutes} />
          <Route path="/sports/bet-delay" component={BetDelay} />
          <Route path="/sports/logged-out" component={LoggedOut} />
          <Route
            path="/account/mitId"
            render={({ location: { state } }) => {
              const locationState = state as Record<string, string>;
              const { path, ...rest } = locationState;

              return <MitIdRedirect returnModal={path} values={rest} />;
            }}
          />
          <Route path="/account" component={AuthRoute} />
          <Route path="/quiz" component={AuthRoute} />
          {!modal.path && <Route component={NotFound} />}
        </Switch>
      </ErrorBoundary>
    </ModalContext.Provider>
  );
};

export default StarsModalRoutes;
